import React, {useState, useRef, useEffect} from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {IconButton} from '@mui/material';
import useClickOutside from '@/hooks/use-click-outside';

type InteractionMode = 'click' | 'hover';

const DEFAULTS = {
  RADIUS: 120,
  DURATION: 300,
  STAGGER: 50,
  OFFSET_ROTATION: 0,
  INTERACTION_MODE: 'click' as InteractionMode,
  HOVER_ON_CLOSE_DELAY: 300,
};

// --- Types ---
export interface RadialMenuItem {
  id: string;
  /** Static content or function that receives isSelected (e.g. for selected vs muted visibility) */
  content: React.ReactNode | ((isSelected: boolean) => React.ReactNode);
  angle?: number;
  style?: React.CSSProperties;
  activeStyle?: React.CSSProperties;
}

export interface RadialMenuProps {
  /** The central element. Acts as the hover/click target. */
  trigger?: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  items: RadialMenuItem[];
  /** Always pass; parent controls selection. Empty = none selected. */
  selectedIds: string[];
  onOptionClick?: (id: string) => void;
  radius?: number;
  duration?: number;
  stagger?: number;
  offsetRotation?: number;
  /** Choose between clicking or hovering to open */
  interactionMode?: 'click' | 'hover';
  /** How long (ms) to wait before closing on mouse leave */
  hoverCloseDelay?: number;
}

// --- Component ---

function RadialMenu({
  trigger,
  items,
  selectedIds,
  onOptionClick,
  radius = DEFAULTS.RADIUS,
  duration = DEFAULTS.DURATION,
  stagger = DEFAULTS.STAGGER,
  offsetRotation = DEFAULTS.OFFSET_ROTATION,
  interactionMode = DEFAULTS.INTERACTION_MODE,
  hoverCloseDelay = DEFAULTS.HOVER_ON_CLOSE_DELAY,
}: RadialMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useClickOutside(() => {
    if (interactionMode === 'click') {
      setIsOpen(false);
    }
  });

  const handleMouseEnter = () => {
    if (interactionMode === 'hover') {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (interactionMode === 'hover') {
      closeTimerRef.current = setTimeout(() => {
        setIsOpen(false);
      }, hoverCloseDelay);
    }
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const getPosition = (index: number, angleOverride?: number) => {
    if (!isOpen) return {x: 0, y: 0, opacity: 0, scale: 0};

    let angleInDegrees = 0;
    if (angleOverride !== undefined) {
      angleInDegrees = angleOverride;
    } else {
      const step = 360 / items.length;
      angleInDegrees = index * step;
    }

    const finalAngle = angleInDegrees + offsetRotation;
    const angleInRad = toRad(finalAngle);

    return {
      x: Math.round(radius * Math.cos(angleInRad)),
      y: Math.round(radius * Math.sin(angleInRad)),
      opacity: 1,
      scale: 1,
    };
  };

  const selectedIdSet = new Set(selectedIds);

  return (
    <div
      ref={wrapperRef}
      className="radial-menu-wrapper"
      data-testid="radial-menu-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {items.map((item, index) => {
        const pos = getPosition(index, item.angle);
        const isSelected = selectedIdSet.has(item.id);
        const itemStyle = {
          ...(isSelected && item.activeStyle ? item.activeStyle : {}),
          ...item.style,
        };

        return (
          <div
            key={item.id}
            data-testid={`radial-menu-item-${item.id}`}
            data-selected={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              onOptionClick?.(item.id);
              if (interactionMode === 'click') {
                setIsOpen(false);
              }
            }}
            style={{
              position: 'absolute',
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${pos.scale})`,
              opacity: pos.opacity,
              zIndex: isOpen ? 5 : 0,
              cursor: 'pointer',
              pointerEvents: isOpen ? 'auto' : 'none',
              transition: `all ${duration}ms cubic-bezier(0.175, 0.885, 0.32, 1.275)`,
              transitionDelay: isOpen ? `${index * stagger}ms` : '0ms',
              ...itemStyle,
            }}
          >
            {typeof item.content === 'function'
              ? item.content(isSelected)
              : item.content}
          </div>
        );
      })}

      <div
        data-testid="radial-menu-trigger"
        onClick={() => {
          if (interactionMode === 'click') {
            setIsOpen(!isOpen);
          }
        }}
        style={{zIndex: 10, cursor: 'pointer', position: 'relative'}}
      >
        {typeof trigger === 'function'
          ? trigger(isOpen)
          : (trigger ?? (
              <IconButton
                size="small"
                sx={defaultTriggerSx(isOpen)}
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
              >
                {isOpen ? <CloseIcon /> : <AddIcon />}
              </IconButton>
            ))}
      </div>
    </div>
  );
}

function defaultTriggerSx(isOpen: boolean) {
  return {
    'width': 40,
    'height': 40,
    'borderRadius': '50%',
    'backgroundColor': isOpen ? 'action.hover' : 'primary.main',
    'color': 'primary.contrastText',
    '&:hover': {
      backgroundColor: isOpen ? 'action.selected' : 'primary.dark',
    },
    'boxShadow': 2,
  };
}

export default RadialMenu;
